/**
 * @author michalstolarski
 * @since 2022-11-7
 */

import * as React from "react";
import styled, { useTheme } from "styled-components";

import { CaretLeft } from "@ui/Assets/Symbolicons/CaretLeft";
import { TestWrapper } from "@ui/Atoms/TestWrapper";
import { Container, Row, Column } from "@ui/Atoms/Grid";
import { Modal } from "@ui/Molecules/Modal2";
import { ModalBody as ModalBodyView } from "@ui/Molecules/Modal";

interface Props {
  testId?: string;
  className?: string;
  children?: React.ReactNode;
  onClose?: <T = HTMLButtonElement>(e?: React.SyntheticEvent<T>) => void;
}

const FullscreenModal = React.memo((props: Props): React.ReactElement => {
  const theme = useTheme();

  return (
    <TestWrapper testId={props.testId}>
      <StyledModal
        cancelText=""
        submitText=""
        customHeader={
          <Header>
            {props.onClose && (
              <Back onClick={props.onClose}>
                <CaretLeft
                  size={theme.icons.sizes.base_x5}
                  variant="line"
                  color="currentColor"
                />
              </Back>
            )}
          </Header>
        }
      >
        <Container>
          <Row>
            <Column>{props.children}</Column>
          </Row>
        </Container>
      </StyledModal>
    </TestWrapper>
  );
});

FullscreenModal.displayName = "FullscreenModal";

export { FullscreenModal };

const StyledModal = styled(Modal)`
  @keyframes slideFromRight {
    from {
      transform: translate(101%, 0);
    }
    to {
      transform: translate(0, 0);
    }
  }

  max-width: 100%;
  max-height: 100%;
  width: 100%;
  height: 100%;
  padding: 0;
  border: none;
  border-radius: 0;
  animation: slideFromRight ${props => props.theme.animations.durationLong}
    ease-out forwards;
  display: grid;
  grid-template-rows: auto 1fr;

  ${ModalBodyView} {
    position: relative;
    max-height: 100%;
    padding: ${props =>
      `0 ${props.theme.margins.base} ${props.theme.margins.base_x3}`};
    overflow: hidden;
    overflow-y: auto;
    scroll-behavior: smooth;

    ${props => props.theme.breakpoints.tablet} {
      padding: 0 0 ${props => props.theme.margins.base_x3};
    }
  }
`;

const Header = styled.nav`
  margin-bottom: ${props => props.theme.margins.base};
  padding: ${props =>
    `${props.theme.margins.base_x2} ${props.theme.margins.base_x2} 0`};

  ${props => props.theme.breakpoints.tablet} {
    padding: ${props => props.theme.margins.base_x2} 0 0;
  }
`;

const Back = styled.button`
  padding: 0;
  color: ${props => props.theme.palette.grey800};
`;
